var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '../icon/Icon.js';
/** Maps the named variants to a base CSS color. Custom colors (hex, rgb)
 *  passed via `variant` are forwarded as-is. */
const VARIANT_BASE = {
    primary: '#0078d4',
    secondary: '#6b7280',
    success: '#28a745',
    warning: '#d4a017',
    danger: '#dc3545'
};
function isVariant(v) {
    return v in VARIANT_BASE;
}
function resolveBase(v) {
    if (!v)
        return VARIANT_BASE.primary;
    return isVariant(v) ? VARIANT_BASE[v] : v;
}
/**
 * Compact status pill — soft pale background, dark text, optional leading
 * dot, optional trailing slotted content (lock icon, chevron, …). The
 * visual matches the status pills used throughout the workflow-strategy
 * module. Switch to `solid` for the legacy saturated look (with optional
 * `special` gradient).
 *
 * `variant` accepts either one of the five preset variant names
 * (`primary` / `secondary` / `success` / `warning` / `danger`) or any CSS
 * color string (`#16a34a`, `rgb(125, 60, 200)`, named CSS color, …).
 */
let BasicStatus = class BasicStatus extends LitElement {
    constructor() {
        super(...arguments);
        this.status = 'Status';
        this.variant = 'primary';
        this.size = 'small';
        this.dot = false;
        this.solid = false;
        this.special = false;
    }
    _iconSize() {
        switch (this.size) {
            case 'large':
                return 15;
            case 'medium':
                return 13;
            case 'small':
            default:
                return 11;
        }
    }
    render() {
        const base = resolveBase(this.variant);
        return html `
      <span
        class="pill"
        role="status"
        aria-label=${this.status}
        style=${`--base: ${base};`}
      >
        ${this.dot ? html `<span class="dot" aria-hidden="true"></span>` : ''}
        <span class="label">${this.status}</span>
        ${this.icon
            ? html `<basic-icon
              class="icon"
              .name=${this.icon}
              size=${this._iconSize()}
            ></basic-icon>`
            : ''}
        <slot></slot>
      </span>
    `;
    }
};
BasicStatus.styles = css `
    :host {
      display: inline-block;
      max-width: 100%;
      font-family: inherit;
    }

    .pill {
      --base: #0078d4;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 5px;
      font-weight: 600;
      white-space: nowrap;
      max-width: 100%;
      border-radius: 999px;
      transition: background 0.15s, color 0.15s, border-color 0.15s;
      box-sizing: border-box;

      /* Default — soft pale-pill (matches strategy module). */
      background: color-mix(in srgb, var(--base) 16%, #ffffff);
      color: color-mix(in srgb, var(--base) 75%, #1a2433);
      border: 1px solid color-mix(in srgb, var(--base) 28%, #ffffff);
    }

    /* Sizes — apply to padding + font-size + dot/icon scale. */
    :host([size='small']) .pill {
      font-size: 11.5px;
      padding: 2px 8px;
      min-height: 22px;
    }
    :host([size='medium']) .pill {
      font-size: 13px;
      padding: 4px 12px;
      min-height: 28px;
    }
    :host([size='large']) .pill {
      font-size: 15px;
      padding: 6px 16px;
      min-height: 36px;
    }

    /* Solid (filled) — bg = base, white text by default. */
    :host([solid]) .pill {
      background: var(--base);
      color: #ffffff;
      border-color: transparent;
    }
    /* Solid + special — gradient from base to a lighter tone. */
    :host([solid][special]) .pill {
      background: linear-gradient(
        135deg,
        var(--base),
        color-mix(in srgb, var(--base) 55%, #ffffff)
      );
    }

    /* Leading dot — full-saturation base color. */
    .dot {
      display: inline-block;
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: var(--base);
      flex-shrink: 0;
    }
    :host([solid]) .dot {
      background: #ffffff;
      opacity: 0.85;
    }
    :host([size='medium']) .dot {
      width: 8px;
      height: 8px;
    }
    :host([size='large']) .dot {
      width: 9px;
      height: 9px;
    }

    /* Block, not flex: text-overflow only works in a block container, so a
       pill narrowed by its container ends the label with "…". */
    .label {
      display: block;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    /* Trailing icon (via the icon prop) — rendered as <basic-icon>. */
    .icon {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      opacity: 0.9;
    }

    /* Trailing slot for status-specific icons (locks, chevrons, …) — used
       when the desired icon isn't part of the basic-icon registry. */
    ::slotted(*) {
      flex-shrink: 0;
      opacity: 0.85;
    }
    ::slotted(svg) {
      width: 11px;
      height: 11px;
    }
    :host([size='medium']) ::slotted(svg) {
      width: 13px;
      height: 13px;
    }
    :host([size='large']) ::slotted(svg) {
      width: 15px;
      height: 15px;
    }
  `;
__decorate([
    property({ type: String })
], BasicStatus.prototype, "status", void 0);
__decorate([
    property({ type: String })
], BasicStatus.prototype, "variant", void 0);
__decorate([
    property({ type: String, reflect: true })
], BasicStatus.prototype, "size", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BasicStatus.prototype, "dot", void 0);
__decorate([
    property({ type: String })
], BasicStatus.prototype, "icon", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BasicStatus.prototype, "solid", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BasicStatus.prototype, "special", void 0);
BasicStatus = __decorate([
    customElement('basic-status')
], BasicStatus);
export { BasicStatus };
//# sourceMappingURL=BasicStatus.js.map